/**
 * Whether the people on this plan can actually build it.
 *
 * THREE ANSWERS, NOT TWO, AND THAT IS THE WHOLE DESIGN. A problem is something wrong. An
 * UNDETERMINED finding is something NOBODY CHECKED -- an operator on a machine no curriculum names,
 * or days held by this plan for somebody who is on none of its steps. Merging the two produces a
 * verdict nobody can ever clear: a tenant that has set no equipment curricula would read as a shop
 * full of untrained operators, and the fix for that is not in the plan.
 *
 * SO `Ready` IS THE ABSENCE OF A PROBLEM, NOT THE ABSENCE OF A FINDING, and this pane shows the
 * undetermined list beside the verdict rather than under it. A green plan with four unchecked
 * findings is a real state and a planner has to be able to see both halves of it at once.
 *
 * UNSTAFFED IS ITS OWN VERDICT. A plan with nobody on it is not ready -- returning READY for it
 * would be the emptiest kind of green, every question passing because none was asked, and it is
 * the state most plans are in before anybody looks at them.
 *
 * EVERY QUESTION IS RE-ASKED ON EVERY READ, because qualification DECAYS: training falls due on a
 * date nobody triggers, a holiday is added after the assignment was made, a curriculum is released
 * naming a machine people were already on, and the plan is re-planned into a different window. None
 * of those is a write, so no write-time check could see them. `AsAt` is shown for that reason --
 * this is an answer about a moment, not a stored status.
 *
 * NOTHING HERE REFUSES ANYTHING, AND NOTHING HERE OFFERS TO. The refusals live at the assignment
 * endpoints; this is the report. An action on this pane that looked like it could fix a finding
 * would be promising something it cannot do.
 *
 * THE WINDOW CAN BE UNKNOWN, AND THEN THE ABSENCE LIST MEANS NOTHING. Without a plan start date
 * there are no dates to look between, so "no absences" and "nowhere to look" are the same empty
 * list -- and the second is the one a planner acts on, by setting a start date.
 */
import React, {useCallback, useContext, useEffect, useState} from "react";
import {Box, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Grey2, Grey5, Grey7, Grey8, Grey17} from "../../theme/styles";

type Finding = {
    Finding?: string | null;
    Why?: string | null;
    StepName?: string | null;
    EquipmentObjectID?: string | null;
};

type Person = {
    AccountUserUSID: string;
    Name?: string | null;
    Email?: string | null;
    Steps?: number;
    Ready?: boolean;
    DaysHeldByThisPlan?: number;
    ListOfFinding?: {Finding?: Finding[] | Finding};
    ListOfOtherHold?: {OtherHold?: any[] | any};
    Load?: any;
};

type Workforce = {
    ObjectID?: string | null;
    Name?: string | null;
    AsAt?: string | null;
    FromDate?: string | null;
    ToDate?: string | null;
    WindowKnown?: boolean;
    Ready?: boolean;
    Verdict?: string | null;
    People?: number;
    ListOfPerson?: {Person?: Person[] | Person};
    ListOfProblem?: {Problem?: string[] | string};
    ListOfUndetermined?: {Undetermined?: string[] | string};
    ListOfOrphanHold?: {OrphanHold?: any[] | any};
    Policy?: {OnUnqualified?: string; OnUngoverned?: string; OnUnavailable?: string;
              IsConfigured?: boolean};
    Schedule?: {LeadTimeDays?: number | null; StepsWithoutHours?: number;
                ShiftConfigured?: boolean; CalendarDaysNotWorkingDays?: boolean};
};

/** A group with one member arrives as an object; the platform serialises every list that way. */
function listOf<T>(held: T[] | T | undefined | null): T[] {
    if (held === null || held === undefined) return [];
    return Array.isArray(held) ? held : [held];
}

function when(value?: string | null): string {
    if (!value) return "—";
    const at = new Date(`${String(value).slice(0, 10)}T00:00:00`);
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleDateString(undefined, {day: "numeric", month: "short", year: "numeric"});
}

function asAt(value?: string | null): string {
    if (!value) return "";
    const at = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(at.getTime())) return String(value);
    return at.toLocaleString(undefined,
                             {day: "numeric", month: "short", hour: "2-digit", minute: "2-digit"});
}

/** The verdict's own words and colour. UNSTAFFED is not a shade of not-ready; it is its own thing. */
const VERDICTS: Record<string, {text: string; colour: string; background: string}> = {
    READY: {text: "Ready", colour: "#1E7B34", background: "#E8F5EA"},
    NOT_READY: {text: "Not ready", colour: "#B3261E", background: "#FCEBEA"},
    UNSTAFFED: {text: "Unstaffed", colour: "#B26A00", background: "#FDF3E3"},
};

export default function BuildPlanWorkforcePane(
    {revisionUSID}: {revisionUSID?: string | null}) {

    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [report, setReport] = useState<Workforce | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(() => {
        if (!revisionUSID) return;
        setLoading(true);
        api.get("t1/v1/build_plan_workforce", {params: {BuildPlanRevisionUSID: revisionUSID}})
            .then((response: any) => {
                setReport(response?.data?.PlatformMessage?.BuildPlanWorkforce ?? null);
                setError(null);
            })
            .catch((problem: any) => {
                setReport(null);
                // A PLAN WITH NO QUANTITY HAS NO WINDOW, so this read cannot answer either. The
                // service's own sentence names the quantity, which is what somebody has to go and
                // set -- a generic failure here would send them looking at the people instead.
                setError(problem?.response?.data?.Cause
                         ?? "This plan's workforce could not be read.");
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [revisionUSID]);

    useEffect(() => { load(); }, [load]);

    if (loading && !report && !error) {
        return <Box sx={{display: "flex", gap: "10px", alignItems: "center", padding: "20px 0"}}>
            <CircularProgress size={18}/>
            <Typography sx={{fontSize: 13, color: Grey7}}>Asking about everyone on the plan…</Typography>
        </Box>;
    }

    if (error) {
        return <Typography sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6, maxWidth: 700,
                                paddingTop: "8px"}}>
            {error}
        </Typography>;
    }

    if (!report) return null;

    const people = listOf<Person>(report.ListOfPerson?.Person);
    const problems = listOf<string>(report.ListOfProblem?.Problem);
    const undetermined = listOf<string>(report.ListOfUndetermined?.Undetermined);
    const verdict = VERDICTS[String(report.Verdict ?? "")]
                    ?? {text: String(report.Verdict ?? "—"), colour: Grey7, background: "#F2F2F2"};

    return (
        <Box sx={{paddingTop: "6px", maxWidth: 1020}}>

            {/* ── the verdict, and when it was true ─────────────────────────────────────────── */}
            <Box sx={{display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap",
                      marginBottom: "6px"}}>
                <Box sx={{background: verdict.background, color: verdict.colour,
                          borderRadius: "4px", padding: "6px 14px", fontSize: 15,
                          fontWeight: 600}}>
                    {verdict.text}
                </Box>
                <Typography sx={{fontSize: 13, color: Grey7}}>
                    {report.People === 1 ? "1 person on this plan"
                                         : `${report.People ?? 0} people on this plan`}
                </Typography>
                <Box sx={{flex: 1}}/>
                {/* AN ANSWER ABOUT A MOMENT, NOT A STORED STATUS. Qualification decays on dates
                    nobody triggers, so a reader has to know how old this answer is. */}
                {report.AsAt && (
                    <Typography sx={{fontSize: 12, color: Grey5}}>
                        asked {asAt(report.AsAt)}
                    </Typography>
                )}
            </Box>

            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 760,
                             marginBottom: "16px"}}>
                {report.Verdict === "UNSTAFFED"
                    ? "Nobody is assigned to any step of this plan. That is not the same as ready: "
                      + "every question below passed because none of them was asked."
                    : report.Verdict === "READY"
                        ? "Nothing is wrong with the people on this plan, over the window it runs "
                          + "in. Anything nobody has checked is listed separately below."
                        : "Something below has to be dealt with before this plan can be built as "
                          + "it stands."}
            </Typography>

            {/* ── the window everything is computed over ────────────────────────────────────── */}
            <Box sx={{display: "flex", gap: "28px", flexWrap: "wrap",
                      border: `1px solid ${Grey2}`, borderRadius: "6px",
                      padding: "12px 16px", marginBottom: "18px"}}>
                <Fact label="From" value={when(report.FromDate)}/>
                <Fact label="To" value={when(report.ToDate)}/>
                <Fact label="Lead time"
                      value={report.Schedule?.LeadTimeDays == null
                             ? "—"
                             : `${report.Schedule.LeadTimeDays} day`
                               + `${report.Schedule.LeadTimeDays === 1 ? "" : "s"}`}/>
            </Box>

            {/* WITHOUT A WINDOW THE ABSENCE LIST MEANS NOTHING, and an empty list would read as
                "nobody is away". Said plainly, with the thing to do about it. */}
            {report.WindowKnown === false && (
                <Typography sx={{fontSize: 13, color: "#B26A00", lineHeight: 1.6, maxWidth: 760,
                                 marginBottom: "16px"}}>
                    This plan has no dates, so there is no window to look in. Absences and the days
                    other plans hold could not be checked at all — that is not the same as nobody
                    being away. Set a plan start date and this answer becomes a real one.
                </Typography>
            )}

            {/* ── what is wrong, and what nobody checked: SIDE BY SIDE, never merged ────────── */}
            <Box sx={{display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "20px"}}>
                <Box sx={{flex: "1 1 360px", minWidth: 300}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8,
                                     marginBottom: "8px"}}>
                        Problems
                    </Typography>
                    {problems.length === 0 ? (
                        <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                            Nothing is wrong with anyone on this plan.
                        </Typography>
                    ) : problems.map(one => (
                        <Typography key={one} sx={{fontSize: 13, color: "#B3261E", lineHeight: 1.6,
                                                   marginBottom: "4px"}}>
                            {one}
                        </Typography>
                    ))}
                </Box>

                <Box sx={{flex: "1 1 360px", minWidth: 300}}>
                    <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8,
                                     marginBottom: "8px"}}>
                        Nobody has checked
                    </Typography>
                    {undetermined.length === 0 ? (
                        <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6}}>
                            Every question that could be asked was asked.
                        </Typography>
                    ) : (
                        <>
                            {undetermined.map(one => (
                                <Typography key={one} sx={{fontSize: 13, color: "#B26A00",
                                                           lineHeight: 1.6, marginBottom: "4px"}}>
                                    {one}
                                </Typography>
                            ))}
                            {/* THE DISTINCTION, SAID ONCE, because it is the one a reader is most
                                likely to collapse. These are not faults in the plan. */}
                            <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.5,
                                             marginTop: "8px"}}>
                                These are not problems with the plan — they are questions this
                                tenant has not given anything to answer with. They do not make the
                                plan unready, and nothing here can clear them.
                            </Typography>
                        </>
                    )}
                </Box>
            </Box>

            {/* ── everyone, and what was found about each ───────────────────────────────────── */}
            <Typography sx={{fontSize: 14, fontWeight: 600, color: Grey8, marginBottom: "10px"}}>
                On the plan
            </Typography>

            {people.length === 0 ? (
                <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.6, maxWidth: 760}}>
                    Nobody is assigned to a step of this plan. People are put on individual steps,
                    on the Steps &amp; schedule tab.
                </Typography>
            ) : people.map(person => {
                const findings = listOf<Finding>(person.ListOfFinding?.Finding);
                const others = listOf<any>(person.ListOfOtherHold?.OtherHold);
                return (
                    <Box key={person.AccountUserUSID}
                         sx={{border: `1px solid ${Grey2}`, borderRadius: "6px",
                              padding: "12px 14px", marginBottom: "10px"}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: "10px",
                                  flexWrap: "wrap"}}>
                            <Typography sx={{fontSize: 14, color: Grey17, fontWeight: 600}}>
                                {person.Name || "Unnamed person"}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey7}}>
                                {person.Steps === 1 ? "on 1 step" : `on ${person.Steps ?? 0} steps`}
                            </Typography>
                            {/* READY HERE MEANS "NO PROBLEM", not "no finding" -- the same rule as
                                the verdict above, and the chip would otherwise contradict the
                                unchecked list beside the person's name. */}
                            {person.Ready === false && (
                                <Chip size="small" label="has a problem"
                                      sx={{height: 20, fontSize: 11, color: "#B3261E",
                                           background: "#FCEBEA"}}/>
                            )}
                            <Box sx={{flex: 1}}/>
                            {!!person.DaysHeldByThisPlan && (
                                <Typography sx={{fontSize: 12, color: Grey5}}>
                                    {person.DaysHeldByThisPlan} day
                                    {person.DaysHeldByThisPlan === 1 ? "" : "s"} held by this plan
                                </Typography>
                            )}
                        </Box>

                        {findings.map((finding, index) => (
                            <Typography key={`${finding.StepName}-${index}`}
                                        sx={{fontSize: 13, lineHeight: 1.6, marginTop: "6px",
                                             color: finding.Finding === "UNQUALIFIED"
                                                    || finding.Finding === "UNAVAILABLE"
                                                    ? "#B3261E" : "#B26A00"}}>
                                {finding.StepName ? `${finding.StepName}: ` : ""}{finding.Why}
                            </Typography>
                        ))}

                        {/* SOMEBODY ELSE'S PLAN ALREADY HAS THEIR DAYS. Not a problem by itself --
                            the same person can be on two plans -- but it is the thing a planner
                            needs before promising a date. */}
                        {!!others.length && (
                            <Tooltip title={others.map((one: any) =>
                                `${one?.ObjectID ?? "another plan"}: ${one?.Days ?? "?"} day(s)`)
                                .join(", ")}>
                                <Typography sx={{fontSize: 12, color: Grey7, marginTop: "6px"}}>
                                    also held by {others.length} other plan
                                    {others.length === 1 ? "" : "s"}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>
                );
            })}

            {/* ── what the tenant's policy would do, which is why a finding is or is not fatal ── */}
            {report.Policy && (
                <Box sx={{marginTop: "16px", paddingTop: "12px",
                          borderTop: `1px solid ${Grey2}`}}>
                    <Typography sx={{fontSize: 12, color: Grey5, lineHeight: 1.6, maxWidth: 760}}>
                        {report.Policy.IsConfigured === false
                            /* A DEFAULT THAT WARNS IS NOT A POLICY SOMEBODY CHOSE, and a reader
                               has to know which of the two they are looking at before they trust
                               a green verdict. */
                            ? "This tenant has not configured a workforce policy, so these "
                              + "findings warn rather than block. Nobody has decided that; it is "
                              + "the default."
                            : `Policy: unqualified ${String(report.Policy.OnUnqualified ?? "")
                                .toLowerCase()}, unchecked ${String(report.Policy.OnUngoverned ?? "")
                                .toLowerCase()}, unavailable ${String(report.Policy.OnUnavailable ?? "")
                                .toLowerCase()}.`}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

function Fact({label, value}: {label: string; value: React.ReactNode}) {
    return (
        <Box>
            <Typography sx={{fontSize: 11, color: Grey5, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>
                {label}
            </Typography>
            <Typography sx={{fontSize: 13, color: Grey17, marginTop: "2px"}}>{value}</Typography>
        </Box>
    );
}
